<template>
  <footer
    v-if="algorithmsData && algorithmsData.length > 0"
    class="bg-white border-top shadow-lg-soft position-fixed bottom-0 w-full z-index-100"
    :style="{ 
      'height': isExpanded ? '450px' : '54px', 
      'display': 'flex', 
      'flex-direction': 'column', 
      'overflow': 'hidden',
      'transition': 'height 0.3s cubic-bezier(0.4, 0, 0.2, 1)' 
    }"
  >
    <div 
      class="px-4 py-2 bg-dark d-flex align-items-center justify-content-between flex-shrink-0 border-bottom cursor-pointer header-hover" 
      style="height: 54px; z-index: 101;"
      @click="togglePanel"
    >
      <div class="d-flex align-items-center flex-nowrap flex-grow-1 overflow-hidden me-4">
        <i :class="['bi', isExpanded ? 'bi-chevron-down' : 'bi-chevron-up', 'text-white opacity-50 me-3']"></i>
        <i class="bi bi-terminal-fill text-primary me-3"></i>
        
        <div class="d-flex align-items-center flex-nowrap flex-grow-1" @click.stop>
          <span class="text-2xs text-muted text-uppercase fw-bold text-nowrap me-2">Job ID:</span>
          <input
            type="text"
            class="form-control form-control-xs bg-dark border-0 text-white font-bold p-0 focus-none"
            style="min-width: 100px; width: 100%;"
            :value="jobName"
            @input="$emit('update:jobName', $event.target.value)"
          />
        </div>
      </div>

      <div class="hstack gap-3 flex-shrink-0" @click.stop>
        <div class="text-2xs text-muted text-nowrap" v-if="submitDisabledReason">
          <i class="bi bi-lock-fill me-1"></i>{{ submitDisabledReason }}
        </div>
        <div class="hstack gap-2 border-start border-white border-opacity-10 ps-4">
          <button
            class="btn btn-primary btn-sm px-6 font-black rounded-1 shadow-none text-nowrap"
            @click="$emit('submit')"
            :disabled="submitFlag"
            :title="submitDisabledReason"
            style="background-color: #5c60f5 !important; font-size: 0.75rem;"
          >
            RUN ANALYSIS <i class="bi bi-chevron-right ms-1"></i>
          </button>
        </div>
      </div>
    </div>

    <div v-show="isExpanded" class="flex-grow-1 d-flex overflow-hidden position-relative">
      <div class="border-end bg-light overflow-y-auto flex-shrink-0 custom-scrollbar" style="width: 160px;">
        <div class="p-2 text-2xs text-muted bg-surface-tertiary border-bottom text-uppercase tracking-wider">Pipeline Flow</div>
        <div
          v-for="algo in algorithmsData"
          :key="algo.id"
          @click="$emit('update:activeAlgoId', algo.id)"
          :class="[
            'px-3 py-3 border-bottom cursor-pointer d-flex align-items-center justify-content-between transition-all',
            activeAlgoId === algo.id ? 'bg-white border-start border-4 border-primary' : 'hover-bg-gray-100 opacity-75'
          ]"
        >
          <div class="overflow-hidden">
            <div class="text-2xs font-black text-dark text-uppercase text-truncate">{{ algo.name }}</div>
            <div class="text-2xs text-muted" style="font-size: 0.5rem;">READY</div>
          </div>
          <i v-if="isModifiedFn && isModifiedFn(algo.id)" class="bi bi-pencil-square text-success ms-1" style="font-size: 0.6rem;"></i>
        </div>
      </div>

      <div class="flex-grow-1 overflow-auto custom-scrollbar bg-white">
        <table class="table table-sm table-hover align-middle mb-0 compact-layout" style="table-layout: fixed; width: 100%; min-width: 850px;">
          <thead class="sticky-top bg-white border-bottom shadow-xs" style="z-index: 10;">
            <tr class="bg-surface-tertiary">
              <th class="ps-4 py-2 text-2xs text-muted font-bold" style="width: 220px;">PARAMETER (WDL key)</th>
              <th class="py-2 text-2xs text-muted font-bold" style="width: 230px;">VALUE</th>
              <th class="py-2 text-2xs text-muted font-bold" style="width: 200px;">DEFAULT</th>
              <th class="pe-4 py-2 text-2xs text-muted font-bold" style="width: 400px;">DESCRIPTION</th>
            </tr>
          </thead>
          <tbody>
                <tr 
                  v-for="p in currentAlgoParams" 
                  :key="p.id" 
                  v-if="p.wdlKey && !p.wdlKey.endsWith('_0')"
                  :class="{ 'bg-soft-primary-light': isParamTouchedFn && isParamTouchedFn(p) }"
                >
              <td class="ps-4 text-truncate">
                <div class="d-flex flex-column">
                  <span class="text-2xs font-bold text-dark">{{ p.wdlKey }}</span>
                  <span class="text-2xs text-muted opacity-75">{{ p.displayName }}</span>
                </div>
              </td>
              <td class="px-1">
                <select v-if="p.isArray && p.enum && p.enum.length" class="form-select form-select-xs border-light" v-model="p.setValue" multiple @change="$emit('save-params', activeAlgoId)">
                  <option v-for="opt in p.enum" :key="String(opt)" :value="opt">{{ opt }}</option>
                </select>
                <select v-else-if="!p.isArray && p.enum && p.enum.length" class="form-select form-select-xs border-light" v-model="p.setValue" @change="$emit('save-params', activeAlgoId)">
                  <option :value="''">(Use Default)</option>
                  <option v-for="opt in p.enum" :key="String(opt)" :value="opt">{{ opt }}</option>
                </select>
                <div v-else-if="p.schemaType === 'boolean'" class="form-check form-switch p-0 m-0 ms-1 d-flex align-items-center">
                  <input class="form-check-input ms-0 scale-75" type="checkbox" v-model="p.setValue" @change="$emit('save-params', activeAlgoId)">
                </div>
                <input v-else :type="p.schemaType === 'integer' || p.schemaType === 'number' ? 'number' : 'text'" class="form-control form-control-xs border-light" v-model="p.setValue" @change="$emit('save-params', activeAlgoId)" :placeholder="stringifyDefaultFn ? stringifyDefaultFn(p.defaultValue) : ''" />
              </td>
              <td class="px-1">
                <code class="text-2xs text-muted bg-transparent opacity-50">{{ stringifyDefaultFn ? stringifyDefaultFn(p.defaultValue) : "" }}</code>
              </td>
              <td class="pe-4 py-1">
                <div class="desc-scroll-box custom-scrollbar-mini text-2xs text-muted border-start ps-2">{{ p.description }}</div>
              </td>
            </tr>
          </tbody>
        </table>
        <div class="p-3 border-top bg-light">
          <div class="text-2xs text-muted mb-1">Preview: Cromwell input.json</div>
          <pre class="m-0 p-2 bg-white border rounded-2 text-2xs" style="max-height: 160px; overflow:auto;">{{ prettyJsonFn ? prettyJsonFn(cromwellInputPreview) : '{}' }}</pre>
        </div>
      </div>
    </div>
  </footer>
</template>

<script>
export default {
  name: "BottomParameterPanel",
  props: {
    algorithmsData: { type: Array, default: () => [] },
    activeAlgoId: { type: [String, Number], default: null },
    jobName: { type: String, default: "" },
    submitFlag: { type: Boolean, default: false },
    submitDisabledReason: { type: String, default: "" },
    currentAlgoParams: { type: Array, default: () => [] },
    cromwellInputPreview: { type: Object, default: () => ({}) },
    isModifiedFn: { type: Function, default: null },
    isParamTouchedFn: { type: Function, default: null },
    stringifyDefaultFn: { type: Function, default: null },
    prettyJsonFn: { type: Function, default: null },
  },
  data() {
    return {
      isExpanded: true,
    };
  },
  methods: {
    togglePanel() {
      this.isExpanded = !this.isExpanded;
    }
  }
};
</script>

<style scoped>
footer {
  box-shadow: 0 -10px 40px rgba(0, 0, 0, 0.25);
  bottom: 0 !important;
  left: 0;
  z-index: 1000;
  overflow: hidden !important;
}
.header-hover:hover {
  background-color: #212529 !important;
}
.cursor-pointer { cursor: pointer; }
.desc-scroll-box { max-height: 42px; overflow-y: auto; line-height: 1.4; padding-right: 4px; word-break: break-word; }
.compact-layout { table-layout: fixed; border-collapse: separate; }
.compact-layout td, .compact-layout th { padding: 0.35rem 0.2rem !important; border-bottom: 1px solid #f1f3f5 !important; }
.custom-scrollbar-mini::-webkit-scrollbar { width: 3px; }
.custom-scrollbar-mini::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 10px; }
/* 关键：确保输入框高度固定且无边框阴影 */
.form-control-xs { 
  height: 24px !important; 
  font-size: 0.85rem !important; 
  padding: 0 4px !important;
  line-height: 1;
}
.form-select-xs { height: 24px !important; font-size: 0.75rem !important; padding: 0 4px !important; }
.scale-75 { transform: scale(0.75); }
.focus-none:focus { outline: none !important; box-shadow: none !important; }
.bg-soft-primary-light { background-color: rgba(92, 96, 245, 0.03); }
.hover-bg-gray-100:hover { background-color: #f8f9fa; cursor: pointer; }
.custom-scrollbar::-webkit-scrollbar { width: 4px; height: 4px; }
.custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e0; border-radius: 10px; }
.text-nowrap { white-space: nowrap; }
</style>